// projectFields.js
// Project-level building facts, laid out per jurisdiction exactly as Section 1
// ("Description of Building / Structure") of that jurisdiction's form.
// Intake and Edit Project show the list for the chosen jurisdiction, in form
// order and wording. Both the electrical and the structural forms print from
// these project fields.
//
// Name on title and owner: one entry prints on both lines. If the owner differs,
// project.ownerDiffers is true and project.ownerName holds the owner.

export const JURISDICTIONS = [
  { value: 'Broward',            label: 'Broward County' },
  { value: 'Miami-Dade',         label: 'Miami-Dade County' },
  { value: 'City of Boca Raton', label: 'City of Boca Raton' },
  { value: 'City of Miami',      label: 'City of Miami' },
];

// 'miami' (Miami-Dade and City of Miami) | 'boca' | 'broward'
export const formKind = (county) => {
  const c = (county || '').toLowerCase();
  if (c.includes('miami')) return 'miami';
  if (c.includes('boca')) return 'boca';
  return 'broward';
};

export const NA = 'N/A';

// Older projects stored the owner in ownerName and had no name-on-title entry.
export const titleName = (p) => p?.nameOnTitle || p?.ownerName || '';
export const ownerOf   = (p) => (p?.ownerDiffers ? (p?.ownerName || '') : titleName(p));

// Field types: text (default), area (multi-line), yesno, address (street, city,
// state, ZIP, plus Bldg. # on the Miami form), title (name on title + owner
// override). `only` limits a field to a discipline's form.
const MIAMI = [
  { item: 'a', key: 'nameOnTitle',       label: 'Name on Title', type: 'title' },
  { item: 'b', key: 'address',           label: 'Building Street Address', type: 'address', bldg: true },
  { item: 'c', key: 'legalDesc',         label: 'Legal Description', type: 'area' },
  { item: 'd', key: 'ownerName',         label: 'Owner’s Name', type: 'owner' },
  { item: 'e', key: 'ownerMailAddr',     label: 'Owner’s Mailing Address' },
  { item: 'f', key: 'ownerEmail',        label: 'Owner’s Email' },
  { item: 'g', key: 'ownerPhone',        label: 'Owner’s Contact Phone Number' },
  { item: 'h', key: 'folio',             label: 'Corresponding Property Folio Number' },
  { item: 'i', key: 'condoAssoc',        label: 'Name of Condominium or Cooperative Association (if applicable)' },
  { item: 'j', key: 'occupancy',         label: 'Building Code Occupancy Classification' },
  { item: 'k', key: 'presentUse',        label: 'Present Use' },
  { item: 'l', key: 'generalDescription', label: 'General description, type of construction, size, number of stories, and special features', type: 'area' },
  { item: 'm', key: 'stories',           label: 'Number of Stories', short: true },
  { item: 'n', key: 'thresholdBuilding', label: 'Is this a Threshold Building as per 553.71(12) F.S.?', type: 'yesno' },
  { item: 'o', key: 'buildingComments',  label: 'Additional Comments', type: 'area', only: 'electrical' },
  { item: 'r', key: 'sqFt',              label: 'Total Building Area (S.F.)', short: true, only: 'structural' },
];

// Boca and Broward: the electrical form has no threshold item, so the letters
// after it shift by one compared with the structural form.
const BOCA = [
  { item: 'a', key: 'nameOnTitle',       label: 'Name on Title', type: 'title' },
  { item: 'b', key: 'address',           label: 'Street Address', type: 'address' },
  { item: 'c', key: 'legalDesc',         label: 'Legal Description', type: 'area' },
  { item: 'd', key: 'ownerName',         label: 'Owner’s Name', type: 'owner' },
  { item: 'e', key: 'ownerMailAddr',     label: 'Owner’s Mailing Address' },
  { item: 'f', key: 'folio',             label: 'Folio Number of Property on which Building is Located' },
  { item: 'g', key: 'occupancy',         label: 'Building Code Occupancy Classification' },
  { item: 'h', key: 'thresholdBuilding', label: 'Is this a Threshold Building as per 553.71(12) F.S.?', type: 'yesno', only: 'structural' },
  { item: { electrical: 'h', structural: 'i' }, key: 'presentUse', label: 'Present Use' },
  { item: { electrical: 'h', structural: 'i' }, key: 'sqFt',       label: 'Square Footage', short: true },
  { item: { electrical: 'h', structural: 'i' }, key: 'stories',    label: 'Number of Stories', short: true },
  { item: { electrical: 'i', structural: 'j' }, key: 'generalDescription', label: 'General Description and Type of Construction', type: 'area' },
  { item: { electrical: 'j', structural: 'k' }, key: 'specialFeatures',    label: 'Special Features', type: 'area' },
  { item: 'k', key: 'buildingComments',  label: 'Additional Comments', type: 'area', only: 'electrical' },
];

const BROWARD = [
  { item: 'a', key: 'nameOnTitle',       label: 'Name on Title', type: 'title' },
  { item: 'b', key: 'address',           label: 'Street Address', type: 'address' },
  { item: 'c', key: 'legalDesc',         label: 'Legal Description', type: 'area' },
  { item: 'd', key: 'ownerName',         label: 'Owner’s Name', type: 'owner' },
  { item: 'e', key: 'ownerMailAddr',     label: 'Owner’s Mailing Address' },
  { item: 'f', key: 'ownerEmail',        label: 'Email Address' },
  { item: 'f', key: 'ownerPhone',        label: 'Contact Number' },
  { item: 'g', key: 'folio',             label: 'Folio Number of Property on which building is located' },
  { item: 'h', key: 'occupancy',         label: 'Building Code Occupancy Classification' },
  { item: 'i', key: 'presentUse',        label: 'Present Use' },
  { item: 'j', key: 'generalDescription', label: 'General Description', type: 'area' },
  { item: 'j', key: 'constructionType',  label: 'Type of Construction' },
  { item: 'k', key: 'sqFt',              label: 'Square Footage', short: true },
  { item: 'k', key: 'stories',           label: 'Number of Stories', short: true },
  { item: 'l', key: 'thresholdBuilding', label: 'Is this a Threshold Building (per F.S. 553.71)?', type: 'yesno', only: 'structural' },
  { item: { electrical: 'l', structural: 'm' }, key: 'specialFeatures', label: 'Special Features', type: 'area' },
  { item: 'm', key: 'buildingComments',  label: 'Additional Comments', type: 'area', only: 'electrical' },
];

const SECTION1 = { miami: MIAMI, boca: BOCA, broward: BROWARD };

// Fields for the selected disciplines. Where the electrical and structural forms
// letter an item differently, both letters are shown ("h (E) / i (S)").
const itemFor = (item, disciplines) => {
  if (typeof item === 'string') return item;
  const ds = disciplines.filter(d => item[d]);
  if (ds.length === 1) return item[ds[0]];
  return ds.map(d => `${item[d]} (${d === 'electrical' ? 'E' : 'S'})`).join(' / ');
};

export const section1Fields = (county, disciplines = ['electrical']) =>
  (SECTION1[formKind(county)] || [])
    .filter(f => !f.only || disciplines.includes(f.only))
    .map(f => ({ ...f, item: itemFor(f.item, disciplines) }));

// Front-page reference number, if the form has one
export const referenceField = (county) => {
  const k = formKind(county);
  if (k === 'miami') return { key: 'permitNumber', label: 'Case Reference Number' };
  return null;
};

// Item number of "Date of Notice of Required Inspection"
export const noticeItem = (county) => (formKind(county) === 'broward' ? '3a' : '2a');

// Value of a Section 1 field for display / printing
export const fieldValue = (p, f) => {
  if (f.type === 'title')   return titleName(p);
  if (f.type === 'owner')   return ownerOf(p);
  if (f.type === 'address') return [p?.address, p?.city, p?.state, p?.zip].filter(Boolean).join(', ') + (f.bldg && p?.buildingNumber ? ` — Bldg. ${p.buildingNumber}` : '');
  return p?.[f.key] ?? '';
};

export const EMPTY_PROJECT = {
  county: '', disciplines: ['electrical'],
  nameOnTitle: '', ownerDiffers: false, ownerName: '',
  address: '', city: '', state: 'FL', zip: '', buildingNumber: '',
  legalDesc: '', ownerMailAddr: '', ownerEmail: '', ownerPhone: '', folio: '',
  condoAssoc: '', occupancy: '', presentUse: '', generalDescription: '', constructionType: '',
  specialFeatures: '', buildingComments: '', sqFt: '', stories: '', thresholdBuilding: '',
  permitNumber: '', noticeDate: '',
};

// Project doc → form state (fills gaps for older projects)
export const projectToForm = (data) => {
  const out = { ...EMPTY_PROJECT };
  Object.keys(EMPTY_PROJECT).forEach(k => { if (data?.[k] !== undefined && data[k] !== null) out[k] = data[k]; });
  out.nameOnTitle = titleName(data);
  out.ownerDiffers = !!data?.ownerDiffers;
  out.ownerName = data?.ownerDiffers ? (data?.ownerName || '') : '';
  return out;
};

// Form state → fields written to the project doc
export const formToProject = (form) => ({
  ...form,
  ownerName: form.ownerDiffers ? form.ownerName : form.nameOnTitle,
});
